interface TableData {
    headers: string[];
    data: string[][];
}

export async function exportTableAsPdf(
    tableData: TableData,
    filename: string = 'export',
    options: {
        orientation?: 'portrait' | 'landscape';
        pageSize?: string;
        headerColor?: string;
        headerTextColor?: string;
        alternatingRowColor?: string;
    } = {},
): Promise<void> {
    try {
        // Import both modules properly
        const [pdfMakeModule, vfsFontsModule] = await Promise.all([
            import('pdfmake/build/pdfmake'),
            import('pdfmake/build/vfs_fonts'),
        ]);

        const pdfMake = pdfMakeModule.default;

        // Set up VFS properly - check different possible structures
        if (
            vfsFontsModule.default &&
            vfsFontsModule.default.pdfMake &&
            vfsFontsModule.default.pdfMake.vfs
        ) {
            pdfMake.vfs = vfsFontsModule.default.pdfMake.vfs;
        } else if (vfsFontsModule.pdfMake && vfsFontsModule.pdfMake.vfs) {
            pdfMake.vfs = vfsFontsModule.pdfMake.vfs;
        } else if (vfsFontsModule.default && typeof vfsFontsModule.default === 'object') {
            pdfMake.vfs = vfsFontsModule.default;
        } else {
            // Last resort - use a simple font configuration without VFS
            pdfMake.fonts = {
                Roboto: {
                    normal: 'Times-Roman',
                    bold: 'Times-Bold',
                    italics: 'Times-Italic',
                    bolditalics: 'Times-BoldItalic',
                },
            };
        }

        const {
            orientation = 'landscape',
            pageSize = 'LEGAL',
            headerColor = '#4472C4',
            headerTextColor = 'white',
            alternatingRowColor = '#F2F2F2',
        } = options;

        const { headers, data } = tableData;

        // Add more detailed debugging
        console.log('PDF Export - Headers:', headers);
        console.log('PDF Export - Data row 1:', data[0]);
        console.log('PDF Export - Data row 2:', data[1]);
        console.log('PDF Export - Headers length:', headers.length);
        console.log('PDF Export - Data length:', data.length);

        // Create table body
        const tableBody: any[][] = [];

        // Header row
        tableBody.push(
            headers.map((header) => ({
                text: header,
                fillColor: headerColor,
                color: headerTextColor,
                fontSize: 11,
                alignment: 'center',
                bold: true,
            })),
        );

        // Data rows - clean up the data and apply alternating row colors
        data.forEach((row, rowIndex) => {
            tableBody.push(
                row.map((cell) => {
                    // Clean up JSON strings and complex data
                    let cleanedCell = cell || '';
                    if (typeof cleanedCell === 'string') {
                        // If it looks like JSON, try to extract meaningful info or simplify
                        if (cleanedCell.startsWith('[{') && cleanedCell.includes('"name"')) {
                            try {
                                const parsed = JSON.parse(cleanedCell);
                                if (Array.isArray(parsed) && parsed.length > 0) {
                                    // Extract names from array of objects
                                    cleanedCell = parsed
                                        .map(
                                            (item) =>
                                                item.name || item.display_name || item.title || '',
                                        )
                                        .filter((n) => n)
                                        .join(', ');
                                }
                            } catch (e) {
                                // If parsing fails, truncate long JSON strings
                                if (cleanedCell.length > 50) {
                                    cleanedCell = cleanedCell.substring(0, 47) + '...';
                                }
                            }
                        }
                    }

                    return {
                        text: cleanedCell,
                        fontSize: 9,
                        fillColor: rowIndex % 2 === 0 ? alternatingRowColor : '#FFFFFF',
                        margin: [2, 2, 2, 2],
                    };
                }),
            );
        });

        console.log('Table body length:', tableBody.length);
        console.log('Table body first row (headers):', tableBody[0]);
        console.log('Table body second row (data):', tableBody[1]);

        const docDefinition = {
            pageSize: pageSize,
            pageOrientation: orientation as 'portrait' | 'landscape',
            pageMargins: [40, 60, 40, 60] as [number, number, number, number],
            defaultStyle: {
                font: 'Roboto',
            },
            content: [
                {
                    text: `${filename} - ${new Date().toLocaleDateString()}`,
                    fontSize: 16,
                    bold: true,
                    margin: [0, 0, 0, 20],
                },
                {
                    table: {
                        headerRows: 1,
                        // Use auto widths for better column sizing
                        widths: Array(headers.length).fill('auto'),
                        body: tableBody,
                    },
                    // Restore custom layout functions for pdfMake 0.2.x
                    layout: {
                        hLineColor: () => '#CCCCCC',
                        vLineColor: () => '#CCCCCC',
                        hLineWidth: () => 0.5,
                        vLineWidth: () => 0.5,
                        paddingLeft: () => 6,
                        paddingRight: () => 6,
                        paddingTop: () => 4,
                        paddingBottom: () => 4,
                    },
                },
            ],
        };

        const pdfFilename = `${filename}_${new Date().toISOString().split('T')[0]}.pdf`;

        pdfMake.createPdf(docDefinition).download(pdfFilename);
    } catch (error) {
        console.error('PDF export failed:', error);
        throw new Error('Failed to export PDF');
    }
}
